import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    FiCheckCircle, FiAlertCircle, FiArrowRight, FiArrowLeft,
} from 'react-icons/fi';
import {
    confirmEmailApi, validateEmailTokenApi,
} from '../../Api';
import { UEN_USER_KEY, getUENUser, isAuthenticatedUEN } from '../../utils';

// Estados do fluxo:
//   'checking' -> validando o token na montagem
//   'ok'       -> token válido, mostra o botão "Confirmar"
//   'success'  -> POST confirm deu certo
//   'invalido' | 'usado' | 'expirado' -> falhas do token
export default function ConfirmarEmail() {
    const [params] = useSearchParams();
    const token = params.get('token') || '';

    const [status, setStatus] = useState('checking');
    const [loading, setLoading] = useState(false);
    const autoTriedRef = useRef(false);

    useEffect(() => {
        if (!token) {
            setStatus('invalido');
            return;
        }
        let cancelado = false;
        (async () => {
            try {
                const res = await validateEmailTokenApi({ token });
                if (cancelado) return;
                if (res.data?.valid) {
                    setStatus('ok');
                } else {
                    setStatus(res.data?.error || 'invalido');
                }
            } catch {
                if (!cancelado) setStatus('invalido');
            }
        })();
        return () => { cancelado = true; };
    }, [token]);

    async function handleConfirm() {
        if (loading) return;
        setLoading(true);
        try {
            await confirmEmailApi({ token });
            setStatus('success');
            // Se o usuário estiver logado, atualiza o flag no localStorage
            // pra o banner sumir sem precisar relogar.
            if (isAuthenticatedUEN()) {
                const u = getUENUser();
                if (u) {
                    localStorage.setItem(UEN_USER_KEY, JSON.stringify({
                        ...u,
                        email_verificado_at: new Date().toISOString(),
                    }));
                }
            }
        } catch (err) {
            const errCode = err.response?.data?.error;
            if (errCode === 'usado' || errCode === 'expirado' || errCode === 'invalido') {
                setStatus(errCode);
            } else {
                setStatus('invalido');
            }
        } finally {
            setLoading(false);
        }
    }

    // Auto-confirma quando o token é válido. Vinha de um link de email,
    // o usuário já mostrou intenção — não faz sentido pedir mais um clique.
    useEffect(() => {
        if (status === 'ok' && !autoTriedRef.current) {
            autoTriedRef.current = true;
            handleConfirm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status]);

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.brand}>
                    <div style={styles.brandLogo}>U</div>
                    <div style={styles.brandText}>
                        <span style={styles.brandName}>UEN</span>
                        <span style={styles.brandTag}>Estudantil</span>
                    </div>
                </div>

                {(status === 'checking' || status === 'ok') && (
                    <div style={styles.centerBox}>
                        <span style={{ ...styles.spinner, borderTopColor: '#059669', borderColor: 'rgba(5,150,105,0.2)', width: 28, height: 28 }} />
                        <h1 style={styles.title}>Confirmando seu email…</h1>
                        <p style={styles.subtitle}>Só um instante.</p>
                    </div>
                )}

                {status === 'success' && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#ECFDF5', color: '#059669' }}>
                            <FiCheckCircle size={36} />
                        </div>
                        <h1 style={styles.title}>Email confirmado!</h1>
                        <p style={styles.subtitle}>
                            A partir de agora você receberá atualizações importantes
                            da UEN sobre sua carteirinha e seus pagamentos.
                        </p>
                        <Link
                            to={isAuthenticatedUEN() ? '/dashboard' : '/login'}
                            style={{
                                ...styles.submit,
                                width: 'auto',
                                display: 'inline-flex',
                                padding: '12px 26px',
                                textDecoration: 'none',
                                marginTop: 8,
                            }}
                        >
                            {isAuthenticatedUEN() ? 'Ir para o painel' : 'Ir para o login'} <FiArrowRight size={16} />
                        </Link>
                    </div>
                )}

                {(status === 'invalido' || status === 'usado' || status === 'expirado') && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#FEF2F2', color: '#DC2626' }}>
                            <FiAlertCircle size={36} />
                        </div>
                        <h1 style={styles.title}>
                            {status === 'expirado' && 'Link expirado'}
                            {status === 'usado' && 'Email já confirmado'}
                            {status === 'invalido' && 'Link inválido'}
                        </h1>
                        <p style={styles.subtitle}>
                            {status === 'expirado' && 'Este link expirou. Entre no app e peça um novo no aviso do topo da página.'}
                            {status === 'usado' && 'Esse link já foi usado uma vez. Se seu email não está confirmado, peça um novo dentro do app.'}
                            {status === 'invalido' && 'O link de confirmação é inválido ou está incompleto.'}
                        </p>
                        <Link
                            to={isAuthenticatedUEN() ? '/dashboard' : '/login'}
                            style={{
                                ...styles.submit,
                                width: 'auto',
                                display: 'inline-flex',
                                padding: '12px 26px',
                                textDecoration: 'none',
                                marginTop: 8,
                            }}
                        >
                            <FiArrowLeft size={16} /> {isAuthenticatedUEN() ? 'Voltar ao painel' : 'Ir para o login'}
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 60%, #F0FDFA 100%)',
        display: 'grid', placeItems: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: '#0F172A',
    },
    card: {
        width: '100%', maxWidth: 440, background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15,23,42,0.04)',
        padding: '40px 36px',
    },
    brand: {
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32,
    },
    brandLogo: {
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center',
        fontWeight: 800, fontSize: 18,
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
    },
    brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandName: { fontWeight: 700, fontSize: '1.1rem', color: '#0F172A' },
    brandTag: { fontSize: '0.75rem', color: '#64748B', marginTop: 2 },

    title: {
        fontSize: '1.6rem', fontWeight: 700, color: '#0F172A',
        margin: '0 0 8px', letterSpacing: '-0.02em',
    },
    subtitle: {
        color: '#64748B', margin: '0 0 24px', fontSize: '0.92rem',
        lineHeight: 1.55,
    },

    submit: {
        padding: '12px 18px',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff', border: 'none', borderRadius: 10,
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, fontFamily: 'inherit',
    },
    spinner: {
        width: 18, height: 18, border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#fff', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 14px',
    },

    centerBox: { textAlign: 'center' },
    iconCircle: {
        width: 72, height: 72, borderRadius: '50%',
        display: 'grid', placeItems: 'center', margin: '0 auto 18px',
    },
};
